import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { format } from "date-fns";
import toast from "react-hot-toast";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  retrieveFastShippingInstructionCostingApi,
  retrieveFastFilteredShippingInstructionCostingApi,
  retrieveFastShippingInstructionsApi,
} from "../../../components/api/ShippingInstructionApiService";
import { retrieveMenuItemsByTypeApi } from "../../../components/api/MenuItemApiService";
import PieChart from "../PieChart";
import BarChart from "../BarChart";

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ constants â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */

const TYPE_PALETTE = [
  "#425078", "#5b7fc7", "#d7e7fc", "#b7b5b5",
  "#7b8396", "#a8c0e8", "#6d7d99", "#c8d8f0",
  "#9bb8e0", "#3d5a9e",
];

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */

const fmt = new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtCost = new Intl.NumberFormat("de-DE", { minimumFractionDigits: 4, maximumFractionDigits: 4 });

function toNum(val) {
  const num = Number(val);
  return Number.isFinite(num) ? num : 0;
}

function toCostNumber(val) {
  return Number(toNum(val).toFixed(4));
}

function formatDate(val) {
  if (!val) return "";
  try { return format(new Date(val), "dd.MM.yyyy"); } catch { return ""; }
}

function formatAmt(val) {
  return val != null ? fmtCost.format(toCostNumber(val)) : "";
}

function normName(val) {
  return String(val ?? "").trim();
}

function getVendorName(si) {
  return normName(
    si?.vendor ??
    si?.purchaseOrderStakeholderName ??
    si?.shipmentRequest?.purchaseOrder?.offer?.stakeholder?.name ??
    si?.shipmentRequest?.purchaseOrder?.stakeholder?.name
  );
}

function getCustomerName(si) {
  return normName(
    si?.customer ??
    si?.salesOrderStakeholderName ??
    si?.shipmentRequest?.salesOrder?.offer?.stakeholder?.name ??
    si?.shipmentRequest?.salesOrder?.customer?.name ??
    si?.shipmentRequest?.salesOrder?.stakeholder?.name
  );
}

/**
 * Flatten a list of ShippingInstructionDto objects into individual claim rows.
 * Each SI can produce up to 2 rows: one for a Debit Note and one for a Credit Note.
 */
function toClaims(siList) {
  const rows = [];
  siList.forEach((si) => {
    const quantity = toNum(si.shipmentQuantity ?? si.shipmentRequest?.shipmentQuantity);
    const quantityUnit = si.shipmentQuantityUnit ?? si.shipmentRequest?.shipmentQuantityUnit ?? "";
    const calcTotal = (unitVal) => {
      if (unitVal == null) return null;
      const unit = toNum(unitVal);
      return quantity > 0 ? unit * quantity : unit;
    };

    const hasDebit  = si.debitNote  != null || si.debitNoteType;
    const hasCredit = si.creditNote != null || si.creditNoteType;
    if (hasDebit) {
      rows.push({
        key: `${si.id}-DEBIT`,
        siId: si.id,
        direction: "DEBIT",
        claimType: si.debitNoteType  || null,
        amount:    si.debitNote,
        currency:  si.debitNoteCurrency,
        amountUsd: si.debitNoteUsd,
        quantity,
        quantityUnit,
        totalAmount: calcTotal(si.debitNote),
        totalAmountUsd: calcTotal(si.debitNoteUsd),
        date:      si.debitNoteDate,
        remark:    si.debitNoteRemark,
        si,
      });
    }
    if (hasCredit) {
      rows.push({
        key: `${si.id}-CREDIT`,
        siId: si.id,
        direction: "CREDIT",
        claimType: si.creditNoteType  || null,
        amount:    si.creditNote,
        currency:  si.creditNoteCurrency,
        amountUsd: si.creditNoteUsd,
        quantity,
        quantityUnit,
        totalAmount: calcTotal(si.creditNote),
        totalAmountUsd: calcTotal(si.creditNoteUsd),
        date:      si.creditNoteDate,
        remark:    si.creditNoteRemark,
        si,
      });
    }
  });
  return rows;
}

function descendingComparator(a, b, key) {
  const va = a[key] ?? "";
  const vb = b[key] ?? "";
  if (vb < va) return -1;
  if (vb > va) return 1;
  return 0;
}
function getComparator(order, key) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, key)
    : (a, b) => -descendingComparator(a, b, key);
}
function stableSort(arr, cmp) {
  return arr
    .map((el, i) => [el, i])
    .sort((a, b) => cmp(a[0], b[0]) || a[1] - b[1])
    .map(([el]) => el);
}

function StatCard({ label, value, sub, color }) {
  const theme = useTheme();
  return (
    <Card elevation={2} sx={{ borderRadius: 2, borderTop: `4px solid ${color || theme.palette.primary.main}` }}>
      <CardContent sx={{ pb: "12px !important" }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.8}>
          {label}
        </Typography>
        <Typography variant="h4" fontWeight={700} mt={0.5}>{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

function TypeChip({ type, typeList }) {
  const idx = typeList.findIndex((t) => t === type);
  const color = TYPE_PALETTE[idx >= 0 ? idx % TYPE_PALETTE.length : 0];
  return (
    <Chip
      label={type ?? ""}
      size="small"
      sx={{ bgcolor: color, color: "#fff", fontWeight: 600, fontSize: 11 }}
    />
  );
}

function DirectionChip({ direction }) {
  return (
    <Chip
      label={direction === "DEBIT" ? "Debit Note" : "Credit Note"}
      size="small"
      color={direction === "DEBIT" ? "warning" : "success"}
      sx={{ fontWeight: 600, fontSize: 11 }}
    />
  );
}

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ expandable detail panel â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
function DetailRow({ label, value }) {
  if (value == null || value === "" || value === "") return null;
  return (
    <Box sx={{ display: "flex", gap: 1, mb: 0.4 }}>
      <Typography variant="caption" color="text.secondary" minWidth={150}>{label}</Typography>
      <Typography variant="caption" fontWeight={500}>{value}</Typography>
    </Box>
  );
}
function SectionTitle({ children }) {
  return (
    <Typography variant="subtitle2" fontWeight={700} color="primary" mb={1}
      sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}>
      {children}
    </Typography>
  );
}

function ClaimDetailPanel({ claim }) {
  const { si, direction } = claim;
  return (
    <Box sx={{ p: 2, bgcolor: "action.hover", borderRadius: 1, mx: 1, mb: 1 }}>
      <Grid container spacing={3}>

        {/* Shipping Instruction */}
        <Grid item xs={12} sm={6} md={3}>
          <SectionTitle>Shipping Instruction</SectionTitle>
          <DetailRow label="SI ID"          value={si.id} />
          <DetailRow label="SI Number"      value={si.finalShippingInstructionNumber ?? si.id} />
          <DetailRow label="SR Type"        value={si.shipmentRequestType ?? si.shipmentRequest?.shipmentRequestType} />
          <DetailRow label="SI Type"        value={si.shippingInstructionType} />
          <DetailRow label="SI Date"        value={formatDate(si.shippingInstructionDate)} />
          <DetailRow label="Status"         value={si.status} />
          <DetailRow label="Vessel"         value={si.vesselName} />
          <DetailRow label="BL Number"      value={si.billOfLadingNumber} />
          <DetailRow label="BL Date"        value={formatDate(si.billOfLadingDate)} />
          <DetailRow label="Port of Load."  value={si.pointOfLoading} />
          <DetailRow label="Port of Disch." value={si.pointOfUnloading} />
          <DetailRow label="ATD"            value={formatDate(si.actualTimeOfDeparture)} />
          <DetailRow label="ATA"            value={formatDate(si.actualTimeOfArrival)} />
          <DetailRow label="Containers"     value={si.containerNumber} />
          <DetailRow label="Destination"    value={si.destination} />
          <DetailRow label="Warehouse"      value={si.warehouse} />
        </Grid>

        {/* Orders */}
        <Grid item xs={12} sm={6} md={3}>
          <SectionTitle>Purchase Order</SectionTitle>
          <DetailRow label="PO ID"       value={si.purchaseOrderId ?? si.shipmentRequest?.purchaseOrder?.id} />
          <DetailRow label="SAP PO Number" value={si.purchaseOrderNumber ?? si.shipmentRequest?.purchaseOrder?.purchaseOrderNumber} />
          <DetailRow label="PO Stakeholder" value={si.purchaseOrderStakeholderName ?? si.shipmentRequest?.purchaseOrder?.offer?.stakeholder?.name ?? si.shipmentRequest?.purchaseOrder?.stakeholder?.name} />
          <DetailRow label="Company"    value={si.purchaseOrderCompany} />
          <DetailRow label="Incoterm"   value={si.purchaseOrderIncoterm} />
          <DetailRow label="Volume"     value={si.purchaseOrderVolume != null ? `${fmt.format(Number(si.purchaseOrderVolume))} ${si.purchaseOrderVolumeUnit ?? ""}`.trim() : undefined} />
          <DetailRow label="Final Price" value={si.purchaseOrderFinalPrice != null ? `${fmt.format(Number(si.purchaseOrderFinalPrice))} ${si.purchaseOrderFinalPriceCurrency ?? ""}`.trim() : undefined} />

          <Divider sx={{ my: 1 }} />
          <SectionTitle>Sales Order</SectionTitle>
          <DetailRow label="SO ID"       value={si.salesOrderId ?? si.shipmentRequest?.salesOrder?.id} />
          <DetailRow label="SAP SO Number" value={si.salesOrderNumber ?? si.shipmentRequest?.salesOrder?.salesOrderNumber} />
          <DetailRow label="SO Stakeholder" value={si.salesOrderStakeholderName ?? si.shipmentRequest?.salesOrder?.offer?.stakeholder?.name ?? si.shipmentRequest?.salesOrder?.customer?.name ?? si.shipmentRequest?.salesOrder?.stakeholder?.name} />
          <DetailRow label="Company"    value={si.salesOrderCompany} />
          <DetailRow label="Incoterm"   value={si.salesOrderIncoterm} />
          <DetailRow label="Volume"     value={si.salesOrderVolume != null ? `${fmt.format(Number(si.salesOrderVolume))} ${si.salesOrderVolumeUnit ?? ""}`.trim() : undefined} />
          <DetailRow label="Final Price" value={si.salesOrderFinalPrice != null ? `${fmt.format(Number(si.salesOrderFinalPrice))} ${si.salesOrderFinalPriceCurrency ?? ""}`.trim() : undefined} />
        </Grid>

        {/* Parties + Item */}
        <Grid item xs={12} sm={6} md={3}>
          <SectionTitle>Parties</SectionTitle>
          <DetailRow label="Shipper"    value={si.shipperName} />
          <DetailRow label="Consignee"  value={si.consigneeName} />
          <DetailRow label="Notify"     value={si.notifyName} />

          <Divider sx={{ my: 1 }} />
          <SectionTitle>Item</SectionTitle>
          <DetailRow label="PO Item ID"     value={si.purchaseOrderItemId ?? si.shipmentRequest?.purchaseOrder?.offer?.item?.id} />
          <DetailRow label="SO Item ID"     value={si.salesOrderItemId ?? si.shipmentRequest?.salesOrder?.offer?.item?.id} />
          <DetailRow label="Buying Grade"   value={si.purchaseOrderItemBuyingGrade ?? si.salesOrderItemBuyingGrade} />
          <DetailRow label="Customer Grade" value={si.shipmentRequestCustomerGradeMark} />
          <DetailRow label="Origin"         value={si.purchaseOrderItemOrigin ?? si.salesOrderItemOrigin} />
          <DetailRow label="Type"           value={si.purchaseOrderItemType ?? si.salesOrderItemType} />
          <DetailRow label="Crop"           value={si.purchaseOrderItemCrop ?? si.salesOrderItemCrop} />
          <DetailRow label="Nicotine"       value={si.purchaseOrderItemNicotine != null ? `${si.purchaseOrderItemNicotine}%` : undefined} />
          <DetailRow label="Sugar"          value={si.purchaseOrderItemSugar != null ? `${si.purchaseOrderItemSugar}%` : undefined} />
          <DetailRow label="Qty"            value={si.shipmentQuantity != null ? `${fmt.format(Number(si.shipmentQuantity))} ${si.shipmentQuantityUnit ?? ""}`.trim() : undefined} />
        </Grid>

        {/* Claim Note Details */}
        <Grid item xs={12} sm={6} md={3}>
          <SectionTitle>{direction === "DEBIT" ? "Debit Note" : "Credit Note"} Details</SectionTitle>
          <DetailRow label="Direction"   value={direction === "DEBIT" ? "Debit Note" : "Credit Note"} />
          <DetailRow label="Type"        value={claim.claimType} />
          <DetailRow label="Date"        value={formatDate(claim.date)} />
          <DetailRow label="Quantity"    value={claim.quantity ? `${fmt.format(claim.quantity)} ${claim.quantityUnit ?? ""}`.trim() : undefined} />
          <DetailRow label="Unit Amount" value={claim.amount != null ? `${formatAmt(claim.amount)} ${claim.currency ?? ""}`.trim() : undefined} />
          <DetailRow label="Total Amount" value={claim.totalAmount != null ? `${formatAmt(claim.totalAmount)} ${claim.currency ?? ""}`.trim() : undefined} />
          <DetailRow label="Unit Amount USD"  value={claim.amountUsd != null ? `$ ${formatAmt(claim.amountUsd)}` : undefined} />
          <DetailRow label="Total Amount USD" value={claim.totalAmountUsd != null ? `$ ${formatAmt(claim.totalAmountUsd)}` : undefined} />
          {claim.remark && (
            <>
              <Divider sx={{ my: 1 }} />
              <SectionTitle>Remark</SectionTitle>
              <Typography variant="caption">{claim.remark}</Typography>
            </>
          )}
          {si.remark && (
            <>
              <Divider sx={{ my: 1 }} />
              <SectionTitle>SI Remark</SectionTitle>
              <Typography variant="caption">{si.remark}</Typography>
            </>
          )}
          {si.siFolderLink && (
            <Box mt={1}>
              <Link href={si.siFolderLink} target="_blank" rel="noopener noreferrer" variant="caption">
                <AttachFileIcon sx={{ fontSize: 12, mr: 0.4 }} />SI Folder
              </Link>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ table head â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const HEAD_CELLS = [
  { id: "expand",    label: "",              sortable: false, width: 40  },
  { id: "direction", label: "Note",          sortable: true,  width: 110 },
  { id: "claimType", label: "Type",          sortable: true,  width: 130 },
  { id: "si.finalShippingInstructionNumber", label: "SI", sortable: false, width: 130 },
  { id: "si.purchaseOrderNumber", label: "PO / SO", sortable: false, width: 130 },
  { id: "si.purchaseOrderCompany", label: "Company", sortable: false, width: 100 },
  { id: "si.purchaseOrderItemBuyingGrade", label: "Item", sortable: false, width: 120 },
  { id: "amount",    label: "Amount",        sortable: true,  width: 120 },
  { id: "amountUsd", label: "USD",           sortable: true,  width: 100 },
  { id: "totalAmountUsd", label: "Total USD", sortable: true, width: 120 },
  { id: "date",      label: "Date",          sortable: true,  width: 100 },
  { id: "remark",    label: "Remark",        sortable: false, width: 200 },
];

function ClaimsTableHead({ order, orderBy, onSort }) {
  return (
    <TableHead>
      <TableRow sx={{ "& th": { fontWeight: 700, whiteSpace: "nowrap", bgcolor: "action.selected" } }}>
        {HEAD_CELLS.map((hc) => (
          <TableCell key={hc.id} width={hc.width} sx={{ py: 1.2 }}>
            {hc.sortable ? (
              <TableSortLabel
                active={orderBy === hc.id}
                direction={orderBy === hc.id ? order : "asc"}
                onClick={() => onSort(hc.id)}
              >
                {hc.label}
                {orderBy === hc.id && (
                  <Box component="span" sx={visuallyHidden}>
                    {order === "desc" ? "sorted descending" : "sorted ascending"}
                  </Box>
                )}
              </TableSortLabel>
            ) : hc.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ chart guard: prevents ApexCharts offsetY error â”€â”€â”€â”€â”€â”€â”€ */
function ChartOrEmpty({ hasData, height = 260, children }) {
  if (!hasData) {
    return (
      <Box sx={{ height, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography color="text.disabled" variant="body2">No data yet</Typography>
      </Box>
    );
  }
  return children;
}

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ main view â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
export default function ClaimsReportView() {
  const theme = useTheme();

  const [siList,     setSiList]     = useState([]);
  const [claimTypes, setClaimTypes] = useState([]);
  const [loading,    setLoading]    = useState(false);

  // filters
  const [filterDirection, setFilterDirection] = useState("ALL");  // ALL | DEBIT | CREDIT
  const [filterTypes,     setFilterTypes]     = useState([]);
  const [filterVendor,    setFilterVendor]    = useState(null);
  const [filterCustomer,  setFilterCustomer]  = useState(null);
  const [dateFrom,        setDateFrom]        = useState("");
  const [dateTo,          setDateTo]          = useState("");

  // table
  const [sortOrder,    setSortOrder]    = useState("desc");
  const [sortBy,       setSortBy]       = useState("date");
  const [page,         setPage]         = useState(0);
  const [rowsPerPage,  setRowsPerPage]  = useState(25);
  const [expandedKey,  setExpandedKey]  = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await retrieveFastShippingInstructionsApi();
      setSiList(res.data ?? []);
    } catch {
      toast.error("Failed to load shipping instruction data.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadClaimTypes = useCallback(async () => {
    try {
      const [debitRes, creditRes] = await Promise.all([
        retrieveMenuItemsByTypeApi("debitNoteType"),
        retrieveMenuItemsByTypeApi("creditNoteType"),
      ]);
      const debitTypes  = (debitRes.data  ?? []).map((m) => m.value ?? m.label);
      const creditTypes = (creditRes.data ?? []).map((m) => m.value ?? m.label);
      setClaimTypes([...new Set([...debitTypes, ...creditTypes])].sort());
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => {
    loadData();
    loadClaimTypes();
  }, [loadData, loadClaimTypes]);

  /* â”€â”€ flatten ACTIVE SIs into claim rows â”€â”€ */
  const activeSiList = useMemo(
    () => siList.filter((si) => String(si?.recordStatus ?? "ACTIVE").toUpperCase() !== "PASSIVE"),
    [siList]
  );

  const allClaims = useMemo(() => toClaims(activeSiList), [activeSiList]);

  const vendorOptions = useMemo(() => {
    return [...new Set(activeSiList.map((si) => getVendorName(si)).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b));
  }, [activeSiList]);

  const customerOptions = useMemo(() => {
    return [...new Set(activeSiList.map((si) => getCustomerName(si)).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b));
  }, [activeSiList]);

  /* â”€â”€ derive all known types from data â”€â”€ */
  const allTypes = useMemo(() => {
    const fromData = [...new Set(allClaims.map((c) => c.claimType).filter(Boolean))];
    return [...new Set([...claimTypes, ...fromData])].sort();
  }, [allClaims, claimTypes]);

  /* â”€â”€ filter â”€â”€ */
  const filteredClaims = useMemo(() => {
    return allClaims.filter((c) => {
      if (filterDirection !== "ALL" && c.direction !== filterDirection) return false;
      if (filterTypes.length && !filterTypes.includes(c.claimType))     return false;
      if (filterVendor && getVendorName(c.si) !== filterVendor)          return false;
      if (filterCustomer && getCustomerName(c.si) !== filterCustomer)    return false;
      if (dateFrom && c.date && new Date(c.date) < new Date(dateFrom))  return false;
      if (dateTo   && c.date && new Date(c.date) > new Date(dateTo))    return false;
      return true;
    });
  }, [allClaims, filterDirection, filterTypes, filterVendor, filterCustomer, dateFrom, dateTo]);

  /* â”€â”€ stats â”€â”€ */
  const stats = useMemo(() => ({
    total:      filteredClaims.length,
    debitCount: filteredClaims.filter((c) => c.direction === "DEBIT").length,
    creditCount:filteredClaims.filter((c) => c.direction === "CREDIT").length,
    profitUsd:  Number(
      filteredClaims
        .filter((c) => c.direction === "DEBIT")
        .reduce((s, c) => s + toNum(c.totalAmountUsd ?? c.amountUsd), 0)
        .toFixed(4)
    ),
    lossUsd:    Number(
      filteredClaims
        .filter((c) => c.direction === "CREDIT")
        .reduce((s, c) => s + toNum(c.totalAmountUsd ?? c.amountUsd), 0)
        .toFixed(4)
    ),
    netUsd:     Number(
      filteredClaims
        .reduce((s, c) => {
          const amount = toNum(c.totalAmountUsd ?? c.amountUsd);
          return s + (c.direction === "DEBIT" ? amount : -amount);
        }, 0)
        .toFixed(4)
    ),
  }), [filteredClaims]);

  /* â”€â”€ chart data â”€â”€ */
  const typeChart = useMemo(() => {
    const counts = {};
    filteredClaims.forEach((c) => {
      const t = c.claimType ?? "Unspecified";
      counts[t] = (counts[t] || 0) + 1;
    });
    return { labels: Object.keys(counts), series: Object.values(counts) };
  }, [filteredClaims]);

  const directionChart = useMemo(() => ({
    categories: ["Profit (Debit)", "Loss (Credit)"],
    data: [stats.profitUsd, stats.lossUsd],
  }), [stats]);

  const amountByTypeChart = useMemo(() => {
    const totals = {};
    filteredClaims.forEach((c) => {
      const t = c.claimType ?? "Unspecified";
      totals[t] = Number(((totals[t] || 0) + toNum(c.totalAmountUsd ?? c.amountUsd)).toFixed(4));
    });
    const entries = Object.entries(totals).sort((a, b) => b[1] - a[1]);
    return { categories: entries.map(([k]) => k), data: entries.map(([, v]) => Number(v.toFixed(4))) };
  }, [filteredClaims]);

  /* â”€â”€ sorting / pagination â”€â”€ */
  const handleSort = (key) => {
    setSortOrder((prev) => (sortBy === key && prev === "asc" ? "desc" : "asc"));
    setSortBy(key);
    setPage(0);
  };

  const sortedClaims = useMemo(
    () => stableSort(filteredClaims, getComparator(sortOrder, sortBy)),
    [filteredClaims, sortOrder, sortBy]
  );

  const visibleClaims = sortedClaims.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const clearFilters = () => {
    setFilterDirection("ALL");
    setFilterTypes([]);
    setFilterVendor(null);
    setFilterCustomer(null);
    setDateFrom("");
    setDateTo("");
    setPage(0);
  };

  const hasFilters = filterDirection !== "ALL" || filterTypes.length || filterVendor || filterCustomer || dateFrom || dateTo;

  const exportClaims = useCallback(async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = "San International";
      workbook.created = new Date();

      const summarySheet = workbook.addWorksheet("Summary", {
        views: [{ showGridLines: false }],
      });

      summarySheet.columns = [
        { width: 28 },
        { width: 38 },
      ];

      summarySheet.mergeCells("A1:B1");
      summarySheet.getCell("A1").value = "Claims Report Export";
      summarySheet.getCell("A1").font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
      summarySheet.getCell("A1").fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF425078" },
      };
      summarySheet.getCell("A1").alignment = { vertical: "middle", horizontal: "left" };
      summarySheet.getRow(1).height = 28;

      const filterTypeText = filterTypes.length ? filterTypes.join(", ") : "All";
      const filterVendorText = filterVendor || "All";
      const filterCustomerText = filterCustomer || "All";
      const fromText = dateFrom || "-";
      const toText = dateTo || "-";

      const summaryRows = [
        ["Generated At", format(new Date(), "dd.MM.yyyy HH:mm")],
        ["Direction Filter", filterDirection],
        ["Type Filter", filterTypeText],
        ["Vendor Filter", filterVendorText],
        ["Customer Filter", filterCustomerText],
        ["Date From", fromText],
        ["Date To", toText],
        ["Total Claims", stats.total],
        ["Debit Notes", stats.debitCount],
        ["Credit Notes", stats.creditCount],
        ["Profit (Debit) USD", stats.profitUsd],
        ["Loss (Credit) USD", stats.lossUsd],
        ["Net USD", stats.netUsd],
      ];

      summaryRows.forEach((row, idx) => {
        const excelRow = summarySheet.addRow(row);
        const rowNo = idx + 3;
        excelRow.getCell(1).font = { bold: true, color: { argb: "FF425078" } };
        excelRow.getCell(2).alignment = { vertical: "middle", horizontal: "left" };
        if (typeof row[1] === "number") {
          excelRow.getCell(2).numFmt = "#,##0.0000";
        }
        if (idx % 2 === 0) {
          summarySheet.getRow(rowNo).fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFF7F9FC" },
          };
        }
      });

      const claimsSheet = workbook.addWorksheet("Claims");
      claimsSheet.views = [{ state: "frozen", ySplit: 1 }];

      claimsSheet.columns = [
        { header: "Note", key: "note", width: 14 },
        { header: "Type", key: "type", width: 20 },
        { header: "Claim Date", key: "claimDate", width: 14 },
        { header: "Quantity", key: "quantity", width: 12 },
        { header: "Qty Unit", key: "quantityUnit", width: 10 },
        { header: "Unit Amount", key: "amount", width: 14 },
        { header: "Total Amount", key: "totalAmount", width: 14 },
        { header: "Currency", key: "currency", width: 10 },
        { header: "Unit Amount USD", key: "amountUsd", width: 14 },
        { header: "Total Amount USD", key: "totalAmountUsd", width: 15 },
        { header: "Profit USD (Total)", key: "profitUsd", width: 17 },
        { header: "Loss USD (Total)", key: "lossUsd", width: 17 },
        { header: "Net Impact USD", key: "netImpactUsd", width: 16 },
        { header: "SI ID", key: "siId", width: 12 },
        { header: "SI Number", key: "siNumber", width: 18 },
        { header: "SR Type", key: "srType", width: 12 },
        { header: "PO ID", key: "poId", width: 12 },
        { header: "SAP PO Number", key: "sapPo", width: 16 },
        { header: "SO ID", key: "soId", width: 12 },
        { header: "SAP SO Number", key: "sapSo", width: 16 },
        { header: "PO Stakeholder", key: "poStakeholder", width: 22 },
        { header: "SO Stakeholder", key: "soStakeholder", width: 22 },
        { header: "PO Item ID", key: "poItemId", width: 14 },
        { header: "SO Item ID", key: "soItemId", width: 14 },
        { header: "Origin", key: "origin", width: 14 },
        { header: "Crop", key: "crop", width: 14 },
        { header: "Company", key: "company", width: 20 },
        { header: "Item", key: "item", width: 20 },
        { header: "Remark", key: "remark", width: 40 },
      ];

      filteredClaims.forEach((claim) => {
        const amount = claim.amount != null ? toCostNumber(claim.amount) : "";
        const totalAmount = claim.totalAmount != null ? toCostNumber(claim.totalAmount) : "";
        const amountUsd = toCostNumber(claim.amountUsd);
        const totalAmountUsd = toCostNumber(claim.totalAmountUsd ?? claim.amountUsd);
        const isDebit = claim.direction === "DEBIT";
        claimsSheet.addRow({
          note: isDebit ? "Debit (Profit)" : "Credit (Loss)",
          type: claim.claimType || "",
          claimDate: formatDate(claim.date),
          quantity: claim.quantity || "",
          quantityUnit: claim.quantityUnit || "",
          amount,
          totalAmount,
          currency: claim.currency || "",
          amountUsd,
          totalAmountUsd,
          profitUsd: isDebit ? totalAmountUsd : 0,
          lossUsd: !isDebit ? totalAmountUsd : 0,
          netImpactUsd: isDebit ? totalAmountUsd : -totalAmountUsd,
          siId: claim.si?.id || "",
          siNumber: claim.si?.finalShippingInstructionNumber ?? claim.si?.id ?? "",
          srType: claim.si?.shipmentRequestType ?? claim.si?.shipmentRequest?.shipmentRequestType ?? "",
          poId: claim.si?.purchaseOrderId ?? claim.si?.shipmentRequest?.purchaseOrder?.id ?? "",
          sapPo: claim.si?.purchaseOrderNumber ?? claim.si?.shipmentRequest?.purchaseOrder?.purchaseOrderNumber ?? "",
          soId: claim.si?.salesOrderId ?? claim.si?.shipmentRequest?.salesOrder?.id ?? "",
          sapSo: claim.si?.salesOrderNumber ?? claim.si?.shipmentRequest?.salesOrder?.salesOrderNumber ?? "",
          poStakeholder: claim.si?.vendor ?? claim.si?.shipmentRequest?.purchaseOrder?.offer?.stakeholder?.name ?? claim.si?.shipmentRequest?.purchaseOrder?.stakeholder?.name ?? "",
          soStakeholder: claim.si?.customer ?? claim.si?.shipmentRequest?.salesOrder?.offer?.stakeholder?.name ?? claim.si?.shipmentRequest?.salesOrder?.customer?.name ?? claim.si?.shipmentRequest?.salesOrder?.stakeholder?.name ?? "",
          poItemId: claim.si?.purchaseOrderItemId ?? claim.si?.shipmentRequest?.purchaseOrder?.offer?.item?.id ?? "",
          soItemId: claim.si?.salesOrderItemId ?? claim.si?.shipmentRequest?.salesOrder?.offer?.item?.id ?? "",
          origin: claim.si?.purchaseOrderItemOrigin ?? claim.si?.salesOrderItemOrigin ?? "",
          crop: claim.si?.purchaseOrderItemCrop ?? claim.si?.salesOrderItemCrop ?? "",
          company: claim.si?.purchaseOrderCompany || claim.si?.salesOrderCompany || "",
          item: claim.si?.purchaseOrderItemBuyingGrade || claim.si?.salesOrderItemBuyingGrade || "",
          remark: claim.remark || "",
        });
      });

      const headerRow = claimsSheet.getRow(1);
      headerRow.height = 22;
      headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
      headerRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF425078" },
      };
      headerRow.alignment = { vertical: "middle", horizontal: "center" };

      claimsSheet.autoFilter = {
        from: "A1",
        to: "AB1",
      };

      claimsSheet.eachRow((row, rowNumber) => {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin", color: { argb: "FFE3E8EF" } },
            left: { style: "thin", color: { argb: "FFE3E8EF" } },
            bottom: { style: "thin", color: { argb: "FFE3E8EF" } },
            right: { style: "thin", color: { argb: "FFE3E8EF" } },
          };
        });
        if (rowNumber > 1 && rowNumber % 2 === 0) {
          row.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFF8FAFD" },
          };
        }
      });

      [4, 5, 7, 8, 9, 10, 11, 12].forEach((col) => {
        claimsSheet.getColumn(col).numFmt = "#,##0.0000";
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const today = format(new Date(), "yyyyMMdd_HHmm");
      saveAs(
        new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
        `claims_report_${today}.xlsx`
      );
      toast.success("Claims report exported.");
    } catch (error) {
      console.error("Claims export failed:", error);
      toast.error("Failed to export claims report.");
    }
  }, [filteredClaims, filterDirection, filterTypes, filterVendor, filterCustomer, dateFrom, dateTo, stats]);

  /* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ render â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>

      {/* â”€â”€ Header â”€â”€ */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Claims Report</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.3}>
            Debit &amp; Credit Notes on Shipping Instructions  linked to SI, PO, SO, items &amp; parties
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Export Excel">
            <IconButton onClick={exportClaims} disabled={loading || filteredClaims.length === 0} color="primary" size="large">
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reload data">
            <IconButton onClick={loadData} disabled={loading} color="primary" size="large">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* â”€â”€ Stat cards â”€â”€ */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <StatCard label="Total Claims"   value={stats.total}
            color={theme.palette.primary.main}
            sub={`${stats.debitCount} debit· ${stats.creditCount} credit`} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard label="Net (USD)"      value={`$ ${formatAmt(stats.netUsd)}`}
            color={theme.palette.info.main} sub="profit (debit) - loss (credit)" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard label="Profit (Debit)" value={`$ ${formatAmt(stats.profitUsd)}`}
            color={theme.palette.success.main} sub={`${stats.debitCount} notes`} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard label="Loss (Credit)"  value={`$ ${formatAmt(stats.lossUsd)}`}
            color={theme.palette.error.main} sub={`${stats.creditCount} notes`} />
        </Grid>
      </Grid>

      {/* â”€â”€ Charts â”€â”€ */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
            <Typography variant="subtitle1" fontWeight={700} mb={1}>By Type</Typography>
            <ChartOrEmpty hasData={typeChart.series.length > 0}>
              <PieChart
                key={typeChart.labels.join(",")}
                labels={typeChart.labels}
                series={typeChart.series}
                donut showTotal totalLabel="Notes"
                height={260}
                chartId="cr-by-type"
              />
            </ChartOrEmpty>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
            <Typography variant="subtitle1" fontWeight={700} mb={1}>Profit vs Loss (USD)</Typography>
            <ChartOrEmpty hasData={stats.total > 0}>
              <BarChart
                key={`${stats.profitUsd}-${stats.lossUsd}`}
                categories={directionChart.categories}
                data={directionChart.data}
                seriesName="USD"
                horizontal
                height={260}
                valueFormatter={(val) => `$ ${formatAmt(val)}`}
                chartId="cr-direction"
              />
            </ChartOrEmpty>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
            <Typography variant="subtitle1" fontWeight={700} mb={1}>USD Amount by Type</Typography>
            <ChartOrEmpty hasData={amountByTypeChart.data.length > 0}>
              <BarChart
                key={amountByTypeChart.categories.join(",")}
                categories={amountByTypeChart.categories}
                data={amountByTypeChart.data}
                seriesName="USD"
                horizontal
                height={260}
                valueFormatter={(val) => `$ ${formatAmt(val)}`}
                chartId="cr-amount-type"
              />
            </ChartOrEmpty>
          </Paper>
        </Grid>
      </Grid>

      <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
          <Typography variant="subtitle2" fontWeight={700}>Filters</Typography>
          {hasFilters && (
            <Tooltip title="Clear all filters">
              <IconButton size="small" onClick={clearFilters} color="warning">
                <FilterAltOffIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Typography variant="body2" color="text.secondary" ml="auto !important">
            Showing <strong>{filteredClaims.length}</strong> of <strong>{allClaims.length}</strong>
          </Typography>
        </Stack>
   <Grid
  container
  spacing={1}
  alignItems="center"
  sx={{
    flexWrap: { xs: "wrap", lg: "nowrap" },
    "& .MuiInputBase-root": {
      minHeight: 38,
    },
  }}
>
  <Grid item xs={12} sm="auto" sx={{ flexShrink: 0 }}>
    <ToggleButtonGroup
      value={filterDirection}
      exclusive
      size="small"
      onChange={(_, v) => { if (v) { setFilterDirection(v); setPage(0); } }}
      sx={{
        "& .MuiToggleButton-root": {
          px: 1.2,
          py: 0.55,
          fontSize: 12,
          whiteSpace: "nowrap",
        },
      }}
    >
      <ToggleButton value="ALL">All</ToggleButton>
      <ToggleButton value="DEBIT" sx={{ color: "warning.main" }}>Debit</ToggleButton>
      <ToggleButton value="CREDIT" sx={{ color: "success.main" }}>Credit</ToggleButton>
    </ToggleButtonGroup>
  </Grid>

  <Grid item xs={12} sm={6} md={3} lg sx={{ minWidth: 160, maxWidth: 220 }}>
    <Autocomplete
      multiple
      size="small"
      limitTags={1}
      options={allTypes}
      value={filterTypes}
      onChange={(_, v) => { setFilterTypes(v); setPage(0); }}
      renderInput={(params) => <TextField {...params} label="Type" />}
      renderTags={(val, getTagProps) =>
        val.map((opt, i) => <Chip key={opt} label={opt} size="small" {...getTagProps({ index: i })} />)
      }
    />
  </Grid>

  <Grid item xs={12} sm={6} md={2.5} lg sx={{ minWidth: 145, maxWidth: 190 }}>
    <Autocomplete
      size="small"
      options={vendorOptions}
      value={filterVendor}
      onChange={(_, v) => { setFilterVendor(v); setPage(0); }}
      renderInput={(params) => <TextField {...params} label="Vendor" />}
    />
  </Grid>

  <Grid item xs={12} sm={6} md={2.5} lg sx={{ minWidth: 145, maxWidth: 190 }}>
    <Autocomplete
      size="small"
      options={customerOptions}
      value={filterCustomer}
      onChange={(_, v) => { setFilterCustomer(v); setPage(0); }}
      renderInput={(params) => <TextField {...params} label="Customer" />}
    />
  </Grid>

  <Grid item xs={6} sm={3} md={2} lg sx={{ minWidth: 135, maxWidth: 155 }}>
    <TextField
      fullWidth
      size="small"
      type="date"
      label="Date From"
      InputLabelProps={{ shrink: true }}
      value={dateFrom}
      onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
    />
  </Grid>

  <Grid item xs={6} sm={3} md={2} lg sx={{ minWidth: 135, maxWidth: 155 }}>
    <TextField
      fullWidth
      size="small"
      type="date"
      label="Date To"
      InputLabelProps={{ shrink: true }}
      value={dateTo}
      onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
    />
  </Grid>
</Grid>
      </Paper>

      {/* â”€â”€ Table â”€â”€ */}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <TableContainer>
          <Table size="small" stickyHeader>
            <ClaimsTableHead order={sortOrder} orderBy={sortBy} onSort={handleSort} />
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={HEAD_CELLS.length} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">Loading</Typography>
                  </TableCell>
                </TableRow>
              ) : visibleClaims.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={HEAD_CELLS.length} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">
                      {allClaims.length === 0
                        ? "No claims found. Debit/Credit Notes on Shipping Instructions appear here."
                        : "No results match the current filters."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visibleClaims.map((claim) => {
                  const isExpanded = expandedKey === claim.key;
                  const { si } = claim;
                  return (
                    <React.Fragment key={claim.key}>
                      <TableRow
                        hover
                        sx={{
                          cursor: "pointer",
                          "& td": { borderBottom: isExpanded ? "none" : undefined },
                          bgcolor: isExpanded ? "action.selected" : undefined,
                        }}
                        onClick={() => setExpandedKey(isExpanded ? null : claim.key)}
                      >
                        <TableCell sx={{ py: 0.8 }}>
                          <IconButton size="small">
                            {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>

                        <TableCell sx={{ py: 0.8 }}>
                          <DirectionChip direction={claim.direction} />
                        </TableCell>

                        <TableCell sx={{ py: 0.8 }}>
                          {claim.claimType ? (
                            <TypeChip type={claim.claimType} typeList={allTypes} />
                          ) : (
                            <Typography variant="caption" color="text.disabled"></Typography>
                          )}
                        </TableCell>

                        <TableCell sx={{ py: 0.8 }}>
                          <Typography variant="caption">{si.finalShippingInstructionNumber ?? si.id}</Typography>
                        </TableCell>

                        <TableCell sx={{ py: 0.8 }}>
                          <Stack>
                            <Typography variant="caption">PO: {si.purchaseOrderNumber ?? si.purchaseOrderId ?? ""}</Typography>
                            <Typography variant="caption">SO: {si.salesOrderNumber ?? si.salesOrderId ?? ""}</Typography>
                          </Stack>
                        </TableCell>

                        <TableCell sx={{ py: 0.8 }}>
                          <Stack>
                            <Typography variant="caption">{si.purchaseOrderCompany ?? ""}</Typography>
                            <Typography variant="caption" color="text.secondary">{si.salesOrderCompany ?? ""}</Typography>
                          </Stack>
                        </TableCell>

                        <TableCell sx={{ py: 0.8 }}>
                          <Typography variant="caption">
                            {si.purchaseOrderItemBuyingGrade ?? si.salesOrderItemBuyingGrade ?? ""}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ py: 0.8 }} align="right">
                          <Typography variant="caption" fontWeight={500}>
                            {claim.amount != null ? `${formatAmt(claim.amount)} ${claim.currency ?? ""}` : ""}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ py: 0.8 }} align="right">
                          <Typography variant="caption" fontWeight={500}
                            color={claim.direction === "DEBIT" ? "warning.main" : "success.main"}>
                            {claim.amountUsd != null ? formatAmt(claim.amountUsd) : ""}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ py: 0.8 }} align="right">
                          <Typography variant="caption" fontWeight={600}
                            color={claim.direction === "DEBIT" ? "warning.dark" : "success.dark"}>
                            {claim.totalAmountUsd != null ? formatAmt(claim.totalAmountUsd) : ""}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ py: 0.8 }}>
                          <Typography variant="caption">{formatDate(claim.date)}</Typography>
                        </TableCell>

                        <TableCell sx={{ py: 0.8, maxWidth: 200 }}>
                          <Tooltip title={claim.remark ?? ""}>
                            <Typography variant="caption" sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {claim.remark ?? ""}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell colSpan={HEAD_CELLS.length} sx={{ p: 0, border: 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <ClaimDetailPanel claim={claim} />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredClaims.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
        />
      </Paper>
    </Box>
  );
}

